import { PageLayout } from '../layout/PageLayout'
import heroAboutUsImage from '@app/assets/images/about-us/hero.jpg'
import hero2AboutUsImage from '@app/assets/images/about-us/hero2.jpg'
import ourMissionImage from '@app/assets/images/about-us/our-mission.jpg'
import ourStoryImage from '@app/assets/images/about-us/our-story.jpg'
import stewardOwnershipImage from '@app/assets/images/planners-table.jpg'
import { Style, styled } from 'inlines'
import { Hero } from '@app/components/blocks/hero/Hero'
import { Quote } from '@app/components/core/typography/Quote'
import { ColumnsSection } from '@app/components/blocks/contentSection/ColumnsSection'
import { OurPartners } from './blocks/OurPartners'
import { OurTeam } from './blocks/OurTeam'
import { Title } from '@app/components/core/typography/Title'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import { useMemo } from 'react'
import { pages } from '@app/util/links'
import { ImageDivider } from '@app/components/core/imageDivider/ImageDivider'
import { useLocation } from 'wouter'

export const AboutUs = () => {
  const isMobile = useMediaQuery()
  const [, setLocation] = useLocation()

  const imageStyle: Style = useMemo(
    () => ({
      height: isMobile ? 'auto' : '31rem',
      width: isMobile ? '100%' : '31rem',
      aspectRatio: '1 / 1',
      objectFit: 'cover',
    }),
    [isMobile],
  )

  return (
    <>
      <PageLayout gap={isMobile ? '2.5rem' : '6.25rem'} marginTop="0">
        <Hero
          title="Een eerlijk en duurzaam woningbouw- systeem"
          backgroundSrc={heroAboutUsImage}
          fullWidth
        />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
          columns={[
            {
              markdown: `## Onze missie\n
Bij CrowdBuilding geloven we dat het woningtekort het beste opgelost kan worden door bewoners zelf.\n
Onze missie? De collectieve zelfbouwbeweging groter maken – in Nederland én op den duur wereldwijd. Waarom? Omdat we vinden dat wonen eerlijker, betaalbaarder en vooral leuker kan. Samen bouwen versterkt niet alleen sociale banden, maar zorgt ook voor veerkrachtige buurten waar mensen écht voor elkaar klaarstaan.\n
Maar daar stopt het niet. We zetten ons ook in voor een gezonde planeet. Door circulair en biobased bouwen te omarmen en te promoten, willen we milieuvriendelijke manieren van bouwen de norm maken.\n
We werken daarom bewust met projecten en professionals die net als wij impact willen maken: op sociaal vlak, voor het klimaat en op financieel gebied. Daarom laten we ons leiden door de principes van de Donuteconomie, waarbij de optimale balans tussen rechtvaardigheid en duurzaamheid leidend is.\n
Zo bouwen we niet alleen huizen, maar ook aan een betere toekomst voor iedereen. Doe je mee?`,
            },
            isMobile
              ? null
              : {
                  imageSrc: ourMissionImage,
                  imageAlt: 'ons missie',
                  imageStyle: {
                    ...imageStyle,
                    marginLeft: 'auto',
                  },
                },
          ]}
        />

        <styled.div style={{ paddingLeft: isMobile ? 0 : '40%' }}>
          <Quote>Wonen kan eerlijker, betaalbaarder en vooral leuker!</Quote>
        </styled.div>

        <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              imageSrc: ourStoryImage,
              imageAlt: 'Ons verhaal',
              imageStyle: {
                ...imageStyle,
                marginRight: 'auto',
              },
            },
            {
              markdown: `## Ons verhaal\n
CrowdBuilding is gebaseerd op 15 jaar ervaring met collectieve zelfbouw projecten. In 2009 richtten Sascha Glasl, Tjeerd Haccou en Marthijn Pool het architectenbureau Space&Matter op, midden in een crisis periode waarin projectontwikkeling vrijwel stil lag. Hierdoor ontstond echter ook de kans om samen met burgercollectieven de eerste CPO-projecten (Collectief Particulier Opdrachtgeverschap) in Amsterdam te realiseren.\n
Hun werk met bewonersinitiatieven, zoals de iconische drijvende wijk Schoonschip in Amsterdam-Noord, liet zien dat collectieven in staat zijn om zeer ambitieuze en innovatieve projecten te realiseren. Deze projecten blinken niet alleen uit op het gebied van duurzaamheid en gemeenschappelijkheid, maar werden ook gebouwd zonder winstoogmerk.\n
Het inzicht dat collectieve woonprojecten veel meerwaarde bieden ten opzichte van standaard woningbouw, vormde de aanleiding voor CrowdBuilding; een online platform gericht op het bekender en toegankelijker maken van collectieve woonvormen.\n
Begin 2024 sloot Brian Garret, oprichter van het succesvolle platform 3DHubs, zich aan als investeerder en co-founder. Met zijn expertise in het bouwen van online platforms vormt hij een cruciale schakel in het team. Samen met een gedreven team werken wij aan de missie om gemeenschappelijk bouwen en wonen de nieuwe standaard te maken.`,
            },
          ]}
        />

        <div>
          <Title.H2
            style={{
              marginBottom: '1.75rem',
              marginTop: 0,
            }}
          >
            Onze partners
          </Title.H2>
          <OurPartners />
        </div>

        <ImageDivider src={hero2AboutUsImage} fullWidth />

        <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              markdown: `## Steward-ownership\n
CrowdBuilding is sinds 29 april 2025 officieel een steward-owned bedrijf:‍\n
- Onze missie staat centraal en is juridisch vastgelegd.
- Zeggenschap ligt bij mensen die actief betrokken zijn bij het werk en het doel van de organisatie.
- Winst is een middel om maatschappelijke impact te bereiken, geen doel op zich. Investeerders krijgen een eerlijk, maar begrensd rendement.
- Gemeenten en partners kunnen bouwen op een stabiele, missiegedreven partner.
- Wooninitiatieven weten dat we structureel aan hun kant staan.\n
Ons doel is om CrowdBuilding in 2035 volledig over te dragen aan Stichting CrowdBuilding, zodat het platform op de lange termijn autonoom, missiegedreven en in dienst van de samenleving blijft opereren.`,
              content: (
                <Button
                  variant="primary"
                  style={{ marginTop: '1rem' }}
                  label="Lees meer"
                  onClick={() => setLocation(pages.stewardOwnership)}
                />
              ),
              style: { alignItems: 'flex-start' },
            },
            {
              imageSrc: stewardOwnershipImage,
              imageAlt: 'steward ownership',
              imageStyle: {
                ...imageStyle,
                marginLeft: 'auto',
              },
            },
          ]}
        />

        <styled.div style={{ paddingLeft: isMobile ? 0 : '40%' }}>
          <Quote>
            Gemeenschappelijke woningbouw voor een eerlijke en inclusieve
            woningmarkt.
          </Quote>
        </styled.div>

        <div>
          <Title.H2
            style={{
              marginBottom: '1.75rem',
              marginTop: 0,
            }}
          >
            Ons team
          </Title.H2>
          <OurTeam />
        </div>

        {/* <ColumnsSection
          style={{ alignItems: 'center' }}
          columns={[
            {
              markdown: `## Join the team\n
Word je enthousiast van onze missie en wil je hieraan bijdragen met jouw energie en talent? Bekijk onze vacaturepagina.\n
_Due to the international nature of our team, our jobs page is partly in English._`,
              content: (
                <Button
                  variant="primary"
                  style={{ marginTop: '1rem' }}
                  label="Vacatures"
                  to={externalPages.jobs}
                />
              ),
              style: { alignItems: 'flex-start' },
            },
            {
              imageSrc: joinTheTeamImage,
              imageAlt: 'join the team',
              imageStyle: {
                ...imageStyle,
                marginLeft: 'auto',
              },
            },
          ]}
        /> */}
      </PageLayout>
    </>
  )
}
